// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductInventoryDto,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tab from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatNumber } from "@mcmc/App/Components/FormattedNumber";
import CheckoutCartLinesContext from "@mcmc/App/Context/CheckoutCartLinesContext";
import loadHoffmanInventoryProductAvailabilities from "@mcmc/App/Handlers/Components/ManufacturerAvailabilty/LoadHoffmanInventory";
import loadPanduitInventoryProductAvailabilities from "@mcmc/App/Handlers/Components/ManufacturerAvailabilty/LoadPanduitInventory";
import loadRockwellInventoryProductAvailabilities from "@mcmc/App/Handlers/Components/ManufacturerAvailabilty/LoadRockwellInventory";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { RegionalInventory } from "@mcmc/App/Store/Reducers";
import { getManufacturerAvailabilityLists } from "@mcmc/App/Store/Selectors/ManufacturerAvailabiltySelectors";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import { labelTextStyle, valueTextStyle } from "@mcmc/App/WidgetExtensions/ProductDetails/ProductDetailsExtensions";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import Notify from "@mcmc/XDL/Components/Notify";
import Ban from "@mcmc/XDL/Icons/Ban";
import Caution from "@mcmc/XDL/Icons/Caution";
import CheckmarkRound from "@mcmc/XDL/Icons/CheckmarkRound";
import Information from "@mcmc/XDL/Icons/Information";
import { mergeWithCss, Roboto } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import parse from "html-react-parser";
import omit from "lodash/omit";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    manufacturerItem?: string;
    inventory?: ProductInventoryDto;
    regionalInventory?: RegionalInventory;
    isEvent?: boolean;
}

interface CustomWarehouseDto extends WarehouseDto {
    properties?: {
        websiteId?: string;
        websiteName?: string;
    };
}

const mapStateToProps = (state: McMcApplicationState) => {
    const settings = getSettingsCollection(state);
    return {
        productSettings: settings.productSettings,
        showInventory: state.context.showInventory,
        isAuthenticated: getSession(state).isAuthenticated,
        siteName: state.context.website.name,
        showBrandsAvailabilityTabs: settings.customSettings?.showBrandsAvailabilityTabs?.toLowerCase() === "true",
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    loadRockwellInventoryProductAvailabilities,
    loadHoffmanInventoryProductAvailabilities,
    loadPanduitInventoryProductAvailabilities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    messageLabelText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    availabilityByWarehouseTableHeaderQty?: DataTableHeaderProps;
    availabilityByWarehouseTableCellQty?: DataTableCellBaseProps;
    tableHeader?: TypographyPresentationProps;
    tableSecondaryHeader?: TypographyPresentationProps;
    spinner?: LoadingSpinnerProps;
    insufficentQtyIcon?: IconPresentationProps;
    insufficentQtyContact?: TypographyPresentationProps;
    regionalInventorySpinner?: TypographyPresentationProps;
    panduitContactUs?: TypographyPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 830,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
        size: 13,
        lineHeight: "18px",
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
        css: css`
            line-height: 18px;
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    availabilityByWarehouseTableHeaderQty: {
        css: css`
            width: 25%;
            text-align: center;
        `,
    },
    availabilityByWarehouseTableCellQty: {
        css: css`
            width: 20%;
            text-align: center;
        `,
    },
    inventoryMessage: {
        css: css`
            display: flex;
        `,
    },
    availabilityByWarehouseLink: {
        color: "secondary",
        typographyProps: {
            ...Roboto,
            weight: 400,
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            margin: 0;
        `,
    },
    tableHeader: {
        css: css`
            overflow-wrap: break-word;
            word-wrap: break-word;
            margin-bottom: 23px;
        `,
        variant: "h1",
        forwardAs: "h3",
    },
    tableSecondaryHeader: {
        weight: "bold",
        css: css`
            letter-spacing: 0;
            margin-top: -10px;
            margin-bottom: 18px;
        `,
        size: 14,
    },
    availabilityByWarehouseTable: {
        css: css`
            margin-bottom: 53px;
            &:last-child {
                margin: 0;
            }
            td,
            th {
                white-space: normal;
            }
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    insufficentQtyIcon: {
        color: "warning",
        size: 13,
        src: Information,
        css: css`
            margin: 2px 5px 0 0;
        `,
    },
    insufficentQtyContact: {
        weight: "bold",
        transform: "uppercase",
        css: css`
            display: block;
        `,
    },
    panduitContactUs: {
        size: 14,
        lineHeight: "20px",
        color: "secondary",
        css: css`
            display: block;
            width: 100%;
        `,
    },
};

const pdpProductAvailabilityStyles: ProductAvailabilityStyles = {
    ...productAvailabilityStyles,
    container: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            &:nth-child(odd) {
                background-color: ${getColor("common.ui3")};
            }
        `,
    },
    availabilityByWarehouseLinkGridItem: {
        width: 12,
        css: [
            valueTextStyle.css as any,
            css`
                display: flex;
                flex-wrap: wrap;
                &:nth-child(odd) {
                    background-color: ${getColor("common.ui3")};
                }

                .sperator {
                    color: ${getColor("common.ui2")};
                    text-align: center;
                    flex: 0 1 45px;
                }
            `,
        ],
    },
    availabilityByWarehouseLink: {
        ...productAvailabilityStyles.availabilityByWarehouseLink,
        color: "primary",
    },
    messageText: mergeWithCss(valueTextStyle, {
        className: "",
    }),
    messageLabelText: labelTextStyle,
    errorText: mergeWithCss(valueTextStyle, {
        className: "",
        css: css`
            ${productAvailabilityStyles.errorText?.css}
        `,
    }),
    realTimeText: mergeWithCss(valueTextStyle, {
        className: "",
        css: css`
            ${productAvailabilityStyles.realTimeText?.css}
        `,
    }),
    regionalInventorySpinner: {
        size: 14,
        weight: 400,
        lineHeight: "20px",
        ...Roboto,
        css: css`
            display: inline;
        `,
    },
};

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return Ban;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return Caution;
    }

    return CheckmarkRound;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const XTabGroup: any = TabGroup;

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    loadRockwellInventoryProductAvailabilities,
    isAuthenticated,
    inventory,
    isEvent,
    regionalInventory,
    siteName,
    signInPageUrl,
    showBrandsAvailabilityTabs,
    loadHoffmanInventoryProductAvailabilities,
    location,
    homePageUrl,
    loadPanduitInventoryProductAvailabilities,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<{ [index: string]: CustomWarehouseDto[] }>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [manufacturerAvailabilityErrorMessage, setManufacturerAvailabilityMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isApiError, setIsApiError] = useState(false);
    const { trackBranchInventory } = useContext(CheckoutCartLinesContext);
    const productContext = useContext(ProductContext);
    const cartLineContext = useContext(CartLineContext);
    const manufacturerItem =
        productContext && productContext.product
            ? productContext.product?.manufacturerItem
            : cartLineContext?.manufacturerItem;
    const properties =
        productContext && productContext.product ? productContext.product?.properties : cartLineContext?.properties;
    const isRockwellItem = properties?.isRockwellItem === "True";
    const isHoffmanItem = properties?.isHoffmanItem === "True";
    const isPanduitProduct = properties?.isPanduitProduct === "True";
    const warehouseErrorSiteMessage = siteMessage("McMc_WarehouseInventoryNotAvaliable") as string;

    const manufacturerAvailabilityDataView = useSelector((state: McMcApplicationState) =>
        getManufacturerAvailabilityLists(state, [productId]),
    );

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                if (result.warehouses) {
                    const filteredData = result.warehouses
                        .filter(item => item.qty && item.qty > 0)
                        .sort((a, b) => `${a.description || a.name}`.localeCompare(`${b.description || b.name}`));
                    if (filteredData.length > 0) {
                        const warehouses = filteredData.reduce(
                            (accum: { [index: string]: CustomWarehouseDto[] }, item: CustomWarehouseDto) => {
                                const websiteName = item.properties?.websiteName;
                                if (websiteName) {
                                    if (!accum[websiteName]) {
                                        accum[websiteName] = [];
                                    }
                                    accum[websiteName].push(item);
                                }
                                return accum;
                            },
                            {},
                        );
                        setWarehouses(warehouses);
                    } else {
                        setErrorMessage(warehouseErrorSiteMessage);
                    }
                }
                if (result.errorMessage && (result as any).isApiError) {
                    setErrorMessage(result.errorMessage);
                    setIsApiError(true);
                } else if (result.errorMessage) {
                    setErrorMessage(warehouseErrorSiteMessage);
                }

                setIsLoading(false);
            },
        });

        if (isAuthenticated) {
            if (isRockwellItem) {
                loadRockwellInventoryProductAvailabilities({
                    productIds: [productId],
                    onError: (error: string) => {
                        if (error) {
                            setManufacturerAvailabilityMessage(error);
                        }
                    },
                });
            } else if (isHoffmanItem) {
                loadHoffmanInventoryProductAvailabilities({
                    productIds: [productId],
                    onError: (error: string) => {
                        if (error) {
                            setManufacturerAvailabilityMessage(error);
                        }
                    },
                });
            } else if (isPanduitProduct) {
                loadPanduitInventoryProductAvailabilities({
                    productIds: [productId],
                    onError: (error: string) => {
                        setManufacturerAvailabilityMessage(error);
                    },
                });
            }
        }
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() =>
        mergeToNew(isProductDetailsPage ? pdpProductAvailabilityStyles : productAvailabilityStyles, extendedStyles),
    );
    if (!showInventory) {
        return null;
    }

    const statusType = properties?.statusType;
    if (statusType !== undefined && !isEvent) {
        let anonymousMessage;
        switch (statusType?.toLowerCase()) {
            case "s":
                anonymousMessage = translate("Normally Stocked");
                break;
            case "o":
                anonymousMessage = translate("Special Order Item");
                break;
            default:
                anonymousMessage = translate("Call For Inventory");
        }

        let returnUrl = location.pathname + location.search;
        if (returnUrl === homePageUrl) {
            returnUrl = `${returnUrl}?homePage=true`;
        }

        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.errorText}>{anonymousMessage}</Typography>
                </GridItem>
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        href={`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`}
                    >
                        {translate("Login For Inventory")}
                    </Link>
                </GridItem>
            </GridContainer>
        );
    }

    if (failedToLoadInventory) {
        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.errorText}>
                        {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                    </Typography>
                </GridItem>
            </GridContainer>
        );
    }

    let inventoryTextComponent = (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>
                <Typography {...styles.realTimeText} />
            </GridItem>
        </GridContainer>
    );
    let regionalInventoryTextComponent = null;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        if (isProductDetailsPage && inventory) {
            if (isEvent) {
                inventoryTextComponent = (
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage-local">
                        <Typography {...styles.messageLabelText}>
                            {translate(
                                "{0} Seats Available",
                                availability.messageType === AvailabilityMessageType.OutOfStock
                                    ? "No"
                                    : `${formatNumber(inventory.qtyOnHand)}`,
                            )}
                        </Typography>
                    </Typography>
                );
            } else {
                inventoryTextComponent = (
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage-local">
                        <Typography {...styles.messageLabelText}>{translate("Local Stock")}:</Typography>
                        {formatNumber(inventory.qtyOnHand)}
                        {inventory.additionalResults?.["localWarehouseName"] &&
                            ` (${inventory.additionalResults?.localWarehouseName})`}
                    </Typography>
                );
                if (regionalInventory) {
                    regionalInventoryTextComponent = (
                        <Typography {...styles.messageText} data-test-selector="availabilityMessage-regional">
                            <Typography {...styles.messageLabelText}>{translate("Regional Stock")}:</Typography>
                            <EllipsisSpinner
                                showSpinner={regionalInventory.isLoading}
                                label={
                                    regionalInventory.isLoading
                                        ? ""
                                        : regionalInventory.amount
                                        ? formatNumber(Number(regionalInventory.amount))
                                        : translate("N/A")
                                }
                                typographyProps={styles.regionalInventorySpinner}
                            ></EllipsisSpinner>
                        </Typography>
                    );
                }
            }
        } else if (trackBranchInventory && availability.messageType === AvailabilityMessageType.OutOfStock) {
            inventoryTextComponent = (
                <StyledWrapper {...styles.inventoryMessage}>
                    <Icon {...styles.insufficentQtyIcon} />
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                        <Typography {...styles.insufficentQtyContact}>{translate("Contact Warehouse")}</Typography>
                        {translate("for Inventory")}
                    </Typography>
                </StyledWrapper>
            );
        } else {
            const message = formatNumber(availability.message);
            inventoryTextComponent = (
                <StyledWrapper {...styles.inventoryMessage}>
                    {/* <Icon color={color} size={13} src={iconSrc} /> Not in the design */}
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                        {parse(message, parserOptions)}
                    </Typography>
                </StyledWrapper>
            );
        }
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    const loading = isLoading || manufacturerAvailabilityDataView.isLoading;
    const manufacturerAvailabilityData = isRockwellItem
        ? manufacturerAvailabilityDataView.value?.filter(inv => inv.manufacturerItem === manufacturerItem)
        : manufacturerAvailabilityDataView.value;
    const showManufacturerAvailabilityInfo = isAuthenticated && (isRockwellItem || isHoffmanItem || isPanduitProduct);

    const renderModalTable = (rows: any, keyLabel: string, showDate = false, isPanduitProduct = false) => {
        return (
            <DataTable {...styles.availabilityByWarehouseTable} data-test-selector="availabilityByWarehouseModal">
                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader} className="no-sticky">
                        {keyLabel}
                    </DataTableHeader>
                    {showDate && <DataTableHeader> {translate("Best Ship Date")}</DataTableHeader>}
                    <DataTableHeader {...styles.availabilityByWarehouseTableHeaderQty}>
                        {translate("{0} Inventory", isPanduitProduct ? "Current" : "")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                    {rows?.map((row: any) => (
                        <DataTableRow {...styles.availabilityByWarehouseTableRow} key={row.name}>
                            <DataTableCell
                                {...styles.availabilityByWarehouseTableCell}
                                className="no-sticky"
                                data-test-selector="warehouseAvailabilityModal_Description"
                            >
                                <Typography as="span">
                                    {`${row.description || row.name} ${
                                        isPanduitProduct ? translate("Business Days") : " "
                                    }`}
                                </Typography>
                                {isPanduitProduct && (
                                    <Typography {...styles.panduitContactUs}>
                                        {translate("Contact us to confirm this as a sourcing option")}
                                    </Typography>
                                )}
                            </DataTableCell>
                            {showDate && <DataTableCell> {row.date}</DataTableCell>}
                            <DataTableCell
                                {...styles.availabilityByWarehouseTableCellQty}
                                data-test-selector="warehouseAvailabilityModal_Quantity"
                            >
                                {isPanduitProduct ? row.formattedQty : formatNumber(row.qty)}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        );
    };

    const othersWarehouse = showBrandsAvailabilityTabs ? omit(warehouses, siteName) : {};
    const workhousesKeys = [...(siteName ? [siteName] : []), ...Object.keys(othersWarehouse)];

    const tabsContent: any[] = [];
    workhousesKeys.forEach((itemKey: string) => {
        if (warehouses[itemKey]) {
            const tab = (
                <Tab headline={`${itemKey} ${translate("Locations")}`} tabKey={itemKey} key={itemKey}>
                    {renderModalTable(warehouses[itemKey], translate("Branch Name"))}
                </Tab>
            );
            tabsContent.push(tab);
        }
    });

    const inventoryError = errorMessage || (tabsContent.length === 0 ? warehouseErrorSiteMessage : "");

    const onLoadContent = (
        <>
            {showManufacturerAvailabilityInfo &&
                (manufacturerAvailabilityData?.length || (!inventoryError && manufacturerAvailabilityErrorMessage)) && (
                    <>
                        <Typography {...styles.tableHeader}>{translate("Manufacturer Inventory")}</Typography>
                        <Typography as="p" {...styles.tableSecondaryHeader}>
                            {isHoffmanItem ? siteMessage("McMc_Hoffman_Availability_Modal_Subheading") : null}
                            {isRockwellItem ? siteMessage("McMc_RockWell_Availability_Modal_Subheading") : null}
                            {isPanduitProduct ? siteMessage("McMc_Panduit_Availability_Modal_Subheading") : null}
                        </Typography>
                        {manufacturerAvailabilityErrorMessage ? (
                            <Notify messageType="danger" hideCloseButton body={manufacturerAvailabilityErrorMessage} />
                        ) : (
                            renderModalTable(
                                manufacturerAvailabilityData,
                                isPanduitProduct
                                    ? translate("Additional Inventory Estimated In")
                                    : translate("Location Name"),
                                isHoffmanItem,
                                isPanduitProduct,
                            )
                        )}
                    </>
                )}

            <Typography id="branch-title" {...styles.tableHeader}>
                {translate("Branch Inventory")}
            </Typography>
            <Typography as="p" {...styles.tableSecondaryHeader}>
                {siteMessage("McMc_Branch_Availability_Modal_Subheading")}
            </Typography>
            {inventoryError ? (
                <Notify messageType={isApiError ? "danger" : "info"} hideCloseButton body={inventoryError} />
            ) : (
                <XTabGroup>{tabsContent}</XTabGroup>
            )}
        </>
    );

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {regionalInventoryTextComponent && (
                <GridItem {...styles.messageGridItem}>{regionalInventoryTextComponent}</GridItem>
            )}
            {showLink(availability, trackInventory && !isEvent, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                        className="viewAvailabilityLink"
                        type="button"
                    >
                        {isProductDetailsPage ? translate("Check All Locations") : translate("View Inventory")}
                    </Link>
                    {isProductDetailsPage && showManufacturerAvailabilityInfo && (
                        <>
                            <span className="sperator" aria-hidden="true">
                                |
                            </span>
                            <Link
                                {...styles.availabilityByWarehouseLink}
                                onClick={viewAvailabilityByWarehouseClickHandler}
                                data-test-selector="availabilityByWarehouseLink-MFG"
                            >
                                {translate("Check MFG's Stock")}
                            </Link>
                        </>
                    )}
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {loading ? (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        ) : (
                            onLoadContent
                        )}
                    </Modal>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
